import { useMutation, useQuery } from "@tanstack/react-query";
import { createFileRoute, useNavigate, useSearch } from "@tanstack/react-router";
import { useState } from "react";

import { AuthLayout, LastUsedBadge } from "../components/auth-layout";
import { Divider } from "../components/divider";
import { GoogleIcon } from "../components/google-icon";
import { authApi } from "../lib/api";
import {
  authErrorMessage,
  AUTH_ERROR_MESSAGES,
  writeLastMethod,
  writeSentAt,
} from "../lib/auth-ui";

export const Route = createFileRoute("/login")({
  // The callbacks redirect here with ?error=<code> when sign-in fails, and ?redirectTo=
  // carries where the visitor was headed before the gate sent them here.
  validateSearch: (
    search: Record<string, unknown>,
  ): { error?: string | undefined; redirectTo?: string | undefined } => ({
    error: typeof search.error === "string" ? search.error : undefined,
    redirectTo: typeof search.redirectTo === "string" ? search.redirectTo : undefined,
  }),
  component: LoginPage,
});

/** Step one: name an address. Step two lives at /login/verify. */
function LoginPage() {
  const { error, redirectTo } = useSearch({ from: "/login" });
  const navigate = useNavigate();
  const [email, setEmail] = useState("");

  const config = useQuery({ queryKey: ["auth", "config"], queryFn: authApi.config });

  const requestLink = useMutation({
    mutationFn: () => authApi.requestMagicLink({ email, redirectTo }),
    onSuccess: () => {
      // The timestamp the resend countdown runs from. It is kept out of the URL: the
      // address is what identifies the pending sign-in, and this is bookkeeping.
      writeSentAt(email, Date.now());
      writeLastMethod("email");
      void navigate({ to: "/login/verify", search: { email, redirectTo } });
    },
  });

  return (
    <AuthLayout>
      <header className="grid gap-2">
        <h1 className="font-elegant text-6xl leading-[1.02] font-medium">Retrouvez vos listes</h1>
        <p className="text-lg text-muted">Pas de mot de passe à retenir.</p>
      </header>

      {error && (
        <p className="text-danger">{AUTH_ERROR_MESSAGES[error] ?? "La connexion a échoué."}</p>
      )}

      {config.data?.google && (
        <>
          {/* A full navigation, not fetch: the OAuth flow needs the browser to follow
              redirects to Google and back. */}
          <a
            href={authApi.googleUrl(redirectTo)}
            onClick={() => writeLastMethod("google")}
            className="btn-secondary relative min-h-13 w-full text-lg"
          >
            <LastUsedBadge method="google" />
            <GoogleIcon />
            Continuer avec Google
          </a>
          <Divider />
        </>
      )}

      <form
        className="relative grid gap-3"
        onSubmit={(event) => {
          event.preventDefault();
          requestLink.mutate();
        }}
      >
        <LastUsedBadge method="email" />
        <input
          className="input min-h-13 text-lg"
          type="email"
          name="email"
          autoComplete="email"
          placeholder="vous@exemple.fr"
          aria-label="Adresse email"
          required
          value={email}
          onChange={(event) => setEmail(event.target.value)}
        />
        <button
          className="btn min-h-13 w-full text-lg"
          type="submit"
          disabled={requestLink.isPending}
        >
          {requestLink.isPending ? "Envoi…" : "Recevoir un lien par e-mail"}
        </button>
      </form>

      {requestLink.isError && (
        <p className="text-danger">
          {authErrorMessage(requestLink.error, "L'email n'a pas pu être envoyé.")}
        </p>
      )}
    </AuthLayout>
  );
}
